import type { InteractionResolver } from "@repo/app-types/content/structured/parser";
import {
  playbookIdSchema,
  type PlaybookId,
} from "@repo/app-types/entities/playbook";
import {
  gradingIdSchema,
  type GradingId,
} from "@repo/app-types/entities/teammatePerformanceSummary";
import type { UIMessage } from "ai";
import { z } from "zod";

/** Reads a tool part's name + output regardless of AI SDK part variant. */
export function getToolOutput(
  part: UIMessage["parts"][number],
): { toolName: string; output: unknown } | undefined {
  const toolName =
    part.type === "dynamic-tool"
      ? part.toolName
      : part.type.startsWith("tool-")
        ? part.type.slice("tool-".length)
        : undefined;

  if (toolName === undefined) {
    return undefined;
  }

  const toolPart = part as { state?: string; output?: unknown };
  if (toolPart.state !== "output-available") {
    return undefined;
  }

  return { toolName, output: toolPart.output };
}

interface ResolvedInteraction {
  gradingId: GradingId;
  playbookId: PlaybookId;
}

/**
 * get-customer-interaction, under its Ask Alpharun and automation toolset keys
 * (see `toolRegistry.ts`). Its result is the interaction itself.
 */
const CUSTOMER_INTERACTION_TOOL_NAMES: ReadonlySet<string> = new Set([
  "getCustomerInteractionTool",
  "getCustomerInteraction",
]);

const customerInteractionSchema = z.object({
  id: gradingIdSchema,
  playbook_id: playbookIdSchema,
});

/** Output keys whose lists hold rows that cite an interaction. */
const INTERACTION_ROW_LIST_KEYS = [
  "customer_interactions",
  "findings",
  "items",
] as const;

const interactionRowSchema = z.object({
  interaction_id: gradingIdSchema,
  playbook_id: playbookIdSchema,
});

const toolOutputSchema = z.record(z.string(), z.unknown());

/**
 * Builds an {@link InteractionResolver} for the `<quote interaction_id="...">`
 * and `<customer interaction_id="...">` tags in an agent's messages from the
 * customer interactions its tool calls returned. The interaction id is the
 * grading id.
 */
export function buildInteractionResolverFromMessages(
  messages: readonly UIMessage[],
): InteractionResolver {
  const interactions = new Map<string, ResolvedInteraction>();

  for (const message of messages) {
    for (const part of message.parts) {
      const tool = getToolOutput(part);
      for (const interaction of tool ? readInteractions(tool) : []) {
        interactions.set(interaction.gradingId, interaction);
      }
    }
  }

  return (interactionId) => interactions.get(interactionId);
}

/** Rows that don't name both a grading and its playbook are skipped. */
function readInteractions(tool: {
  toolName: string;
  output: unknown;
}): readonly ResolvedInteraction[] {
  if (CUSTOMER_INTERACTION_TOOL_NAMES.has(tool.toolName)) {
    const interaction = customerInteractionSchema.safeParse(tool.output).data;
    return interaction
      ? [{ gradingId: interaction.id, playbookId: interaction.playbook_id }]
      : [];
  }

  const output = toolOutputSchema.safeParse(tool.output).data ?? {};
  const rows = INTERACTION_ROW_LIST_KEYS.map((key) => output[key]).find(
    Array.isArray,
  );

  return (rows ?? []).flatMap((row) => {
    const parsed = interactionRowSchema.safeParse(row).data;
    return parsed
      ? [{ gradingId: parsed.interaction_id, playbookId: parsed.playbook_id }]
      : [];
  });
}
